/**
 * Incidents rail surface (#387) — the discoverable "where do tracked incidents go?"
 * home. After an operator declares an incident (from a fire / hotspot / community
 * body), it was reachable again only by re-finding that source on the map. This
 * lists the OPEN tracked incidents on a persistent Coordination sub-tab: each row
 * shows the hazard badge, name + incident number, and open/closed status; clicking
 * a row opens the existing IncidentManagementSection (operational periods + SITREP
 * chain) in the detail panel, and a "Fly to" button re-centres the COP on it.
 *
 * R5 Area Command Slice 2 (#494) — the PEOC duty officer's Area Command OVERSIGHT lives
 * HERE (coordination), NOT in the IC's tactical work surface (§4.6). Three additions:
 *   - grouping PROPOSALS over ungrouped open wildfire incidents (establish + place; a 409
 *     drops that fire and re-renders — NEVER auto-merges, NEVER fails the establish);
 *   - the established Area Commands (open one to see/manage its members);
 *   - a "Under Area Command — <name>" badge on each grouped child row (opens the parent).
 *
 * Polled on the shared 30s cadence; a local reload counter re-renders after a confirm.
 */
import { useEffect, useMemo, useState } from 'react';
import type { AreaCommandProposal, Incident } from '@tsr/shared-types';
import {
  establishAreaCommand,
  fetchAreaCommandProposals,
  fetchAreaCommands,
  fetchIncidents,
  placeUnderAreaCommand,
} from './api';
import { getSessionId } from './lib/operatorActions';
import { hazardStyle, incidentStatusStyle } from './styles/incidentStyles';
import {
  AREA_COMMAND_HEADING,
  ESTABLISH_AREA_COMMAND_LABEL,
  PLACE_UNDER_AREA_COMMAND_LABEL,
  UNDER_AREA_COMMAND_PREFIX,
  addToAreaCommandProposalPrompt,
  areaCommandProposalRationale,
  establishAreaCommandProposalPrompt,
  underAreaCommandLabel,
  RECENTLY_DEACTIVATED_HEADING,
  RECENTLY_DEACTIVATED_EMPTY,
  RECENTLY_DEACTIVATED_HELP,
} from './incidentVocabulary';

/** Passive re-poll cadence (mirrors the AnomalyQueue / DroneRequests 30s tick). */
export const INCIDENTS_POLL_MS = 30_000;

/** #485 — the operator-adjustable "Recently deactivated" findability window (days). Default 30
 *  spans the common re-emergence horizon; wider than the 7-day protective-action reignite window
 *  because an operator may reopen a thread weeks later. */
export const DEACTIVATED_WINDOW_OPTIONS = [7, 14, 30, 90] as const;
export const DEFAULT_DEACTIVATED_WINDOW_DAYS = 30;

/** Compact "Nd/Nh ago" for a deactivation instant (closed_at). */
function deactivatedAgo(closedAt: string | null): string {
  if (!closedAt) return '';
  const s = Math.max(0, (Date.now() - new Date(closedAt).getTime()) / 1000);
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 172_800) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86_400)}d ago`;
}

export function IncidentsPanel({
  onSelectIncident,
  onFlyTo,
  onActiveCountChange,
}: {
  /** Open an incident's record (operational periods + SITREP chain) — reuses the
   *  COP's {kind:'incident'} SidePanel arm. Also opens an Area Command's member list. */
  onSelectIncident?: (incident: Incident) => void;
  /** Re-centre the COP camera on the incident centroid (reuses flyToIncident). */
  onFlyTo?: (lat: number, lon: number) => void;
  /** Report the count of OPEN tracked incidents up for the sub-tab badge. */
  onActiveCountChange?: (count: number) => void;
}): JSX.Element {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [areaCommands, setAreaCommands] = useState<Incident[]>([]);
  const [proposals, setProposals] = useState<AreaCommandProposal[]>([]);
  // #485 FINDABILITY — the recently-deactivated incidents (leave the open list + map; kept
  // findable here so a re-emergence reopens the same thread instead of minting a duplicate).
  const [deactivated, setDeactivated] = useState<Incident[]>([]);
  const [deactWindow, setDeactWindow] = useState<number>(DEFAULT_DEACTIVATED_WINDOW_DAYS);
  const [deactOpen, setDeactOpen] = useState(false);
  // Dismissals persist across refetches (proposal_id is stable over the member set).
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [editNames, setEditNames] = useState<Record<string, string>>({});
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The panel stays mounted in the rail (the Coordination group hides inactive sub-tabs
  // with CSS rather than unmounting), so this polls regardless of the active tab. `reload`
  // is bumped after a confirm to re-render immediately (mirrors IncidentManagementSection).
  const [tick, setTick] = useState(0);
  const [reload, setReload] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), INCIDENTS_POLL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const ctl = new AbortController();
    void fetchIncidents({ status: 'open', limit: 100 }, ctl.signal)
      .then(setIncidents)
      .catch(() => undefined);
    void fetchAreaCommands(ctl.signal)
      .then(setAreaCommands)
      .catch(() => undefined);
    void fetchAreaCommandProposals(ctl.signal)
      .then(setProposals)
      .catch(() => undefined);
    // #485 — recently-deactivated (status=closed + lifecycle_state=deactivated, within window).
    void fetchIncidents(
      {
        status: 'closed',
        lifecycle_state: 'deactivated',
        deactivated_within_days: deactWindow,
        limit: 50,
      },
      ctl.signal,
    )
      .then(setDeactivated)
      .catch(() => undefined);
    return () => ctl.abort();
  }, [tick, reload, deactWindow]);

  useEffect(() => {
    onActiveCountChange?.(incidents.length);
  }, [incidents, onActiveCountChange]);

  const acById = useMemo(() => {
    const m = new Map<string, Incident>();
    for (const ac of areaCommands) m.set(ac.id, ac);
    return m;
  }, [areaCommands]);

  const visibleProposals = proposals.filter((p) => !dismissed.has(p.proposal_id));

  async function confirmProposal(p: AreaCommandProposal): Promise<void> {
    setBusyId(p.proposal_id);
    setError(null);
    try {
      const sessionId = getSessionId();
      let parentId: string;
      if (p.existing_area_command) {
        parentId = p.existing_area_command.id;
      } else {
        const name = (editNames[p.proposal_id] ?? p.suggested_name).trim() || p.suggested_name;
        const ac = await establishAreaCommand(
          { name, lon: p.centroid.lon, lat: p.centroid.lat },
          sessionId,
        );
        parentId = ac.id;
      }
      // Place each member; a 409 (the fire moved under a DIFFERENT Area Command between
      // render and confirm) DROPS that fire and continues — never fails the establish.
      let skipped = 0;
      for (const m of p.members) {
        const r = await placeUnderAreaCommand(parentId, m.id, sessionId);
        if (r.conflict) skipped += 1;
      }
      if (skipped > 0) {
        setError(
          `${skipped} ${skipped === 1 ? 'fire' : 'fires'} had moved under another Area Command and ${
            skipped === 1 ? 'was' : 'were'
          } skipped.`,
        );
      }
      setReload((n) => n + 1); // re-render proposals + Area Commands + incident rows
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  function dismissProposal(id: string): void {
    setDismissed((prev) => new Set(prev).add(id));
  }

  return (
    <div className="px-3 py-3" data-testid="incidents-panel">
      {/* ── Area Command grouping PROPOSALS (§4.6 — PEOC duty officer) ─── */}
      {visibleProposals.length > 0 && (
        <div className="mb-3 space-y-2" data-testid="area-command-proposals">
          {visibleProposals.map((p) => {
            const isAdd = !!p.existing_area_command;
            const heading = isAdd
              ? addToAreaCommandProposalPrompt(p.members.length, p.existing_area_command!.name)
              : establishAreaCommandProposalPrompt(p.members.length);
            const busy = busyId === p.proposal_id;
            return (
              <div
                key={p.proposal_id}
                data-testid="area-command-proposal"
                className="rounded border border-indigo-500/50 bg-indigo-500/10 px-3 py-2"
              >
                <p
                  className="text-[12px] font-semibold text-indigo-100"
                  data-testid="area-command-proposal-heading"
                >
                  {heading}
                </p>
                <p
                  className="mt-0.5 text-[11px] text-indigo-200/80"
                  data-testid="area-command-proposal-rationale"
                >
                  {areaCommandProposalRationale(p.kind, p.members.length, p.spread_km)}
                </p>
                <ul className="mt-1.5 space-y-0.5">
                  {p.members.map((m) => {
                    const hz = hazardStyle(m.hazard_type);
                    return (
                      <li
                        key={m.id}
                        className="flex items-center gap-1.5 text-[11px] text-slate-200"
                      >
                        <span aria-hidden style={{ color: hz.color }}>
                          {hz.icon}
                        </span>
                        <span className="min-w-0 flex-1 truncate">{m.name}</span>
                        <span className="shrink-0 font-mono text-[10px] text-slate-400">
                          {m.incident_number}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {!isAdd && (
                  <label className="mt-2 block">
                    <span className="text-[10px] uppercase tracking-wider text-slate-400">Name</span>
                    <input
                      type="text"
                      data-testid="area-command-proposal-name"
                      value={editNames[p.proposal_id] ?? p.suggested_name}
                      onChange={(e) =>
                        setEditNames((prev) => ({ ...prev, [p.proposal_id]: e.target.value }))
                      }
                      className="mt-0.5 w-full rounded border border-slate-600 bg-slate-900 px-2 py-1 text-[12px] text-slate-100"
                    />
                  </label>
                )}
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    data-testid="area-command-proposal-confirm"
                    disabled={busy}
                    onClick={() => void confirmProposal(p)}
                    className="rounded bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {busy ? '…' : isAdd ? PLACE_UNDER_AREA_COMMAND_LABEL : ESTABLISH_AREA_COMMAND_LABEL}
                  </button>
                  <button
                    type="button"
                    data-testid="area-command-proposal-dismiss"
                    disabled={busy}
                    onClick={() => dismissProposal(p.proposal_id)}
                    className="rounded border border-slate-600 px-2 py-1 text-[11px] text-slate-300 hover:bg-slate-800 disabled:opacity-50"
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            );
          })}
          {error && (
            <p className="text-[11px] text-amber-300" data-testid="area-command-proposal-error">
              {error}
            </p>
          )}
        </div>
      )}

      {/* ── Established Area Commands (open one to see/manage members) ─── */}
      {areaCommands.length > 0 && (
        <div className="mb-3" data-testid="area-commands-section">
          <h4 className="mb-1 text-[10px] uppercase tracking-wider text-indigo-300">
            {AREA_COMMAND_HEADING} · {areaCommands.length}
          </h4>
          <ul className="space-y-1">
            {areaCommands.map((ac) => (
              <li key={ac.id} data-testid="area-command-row">
                <button
                  type="button"
                  data-testid="area-command-open"
                  onClick={() => onSelectIncident?.(ac)}
                  className="flex w-full items-center gap-2 rounded border border-indigo-500/40 bg-indigo-500/5 px-3 py-1.5 text-left hover:bg-indigo-500/15"
                >
                  <span className="shrink-0 rounded bg-indigo-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-indigo-200">
                    {AREA_COMMAND_HEADING}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-100">
                    {ac.name}
                  </span>
                  <span className="shrink-0 font-mono text-[10px] text-slate-400">
                    {ac.incident_number}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Open tracked incidents ─────────────────────────────────────── */}
      <p className="mb-2 text-[11px] leading-relaxed text-slate-400">
        Open tracked incidents. Click one to open its record (operational periods + SITREP chain);
        “Fly to” re-centres the map on it. Updates automatically.
      </p>
      {incidents.length === 0 ? (
        <p className="text-[12px] text-slate-500" data-testid="incidents-empty">
          No open incidents.
        </p>
      ) : (
        <ul className="space-y-2">
          {incidents.map((inc) => {
            const hz = hazardStyle(inc.hazard_type);
            const st = incidentStatusStyle(inc.status);
            const parentAc = inc.parent_incident_id ? acById.get(inc.parent_incident_id) : undefined;
            return (
              <li
                key={inc.id}
                data-testid="incident-row"
                className="rounded border border-slate-700 bg-slate-800/40"
              >
                <button
                  type="button"
                  data-testid="incident-open"
                  onClick={() => onSelectIncident?.(inc)}
                  className="block w-full px-3 py-2 text-left hover:bg-slate-800"
                >
                  <div className="flex items-center gap-2">
                    <span
                      className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                      style={{ color: hz.color, backgroundColor: `${hz.color}22` }}
                    >
                      <span aria-hidden>{hz.icon}</span> {hz.label}
                    </span>
                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-slate-100">
                      {inc.name}
                    </span>
                    <span
                      className="shrink-0 text-[10px] font-medium"
                      style={{ color: st.color }}
                      title={st.label}
                    >
                      <span aria-hidden>{st.icon}</span> {st.label}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-[11px] text-slate-400">
                    {inc.incident_number}
                    {inc.linked_community_count
                      ? ` · ${inc.linked_community_count} ${
                          inc.linked_community_count === 1 ? 'community' : 'communities'
                        }`
                      : ''}
                  </p>
                </button>
                {inc.parent_incident_id && (
                  <div className="px-3 pb-1">
                    <button
                      type="button"
                      data-testid="incident-under-area-command"
                      disabled={!parentAc}
                      onClick={() => parentAc && onSelectIncident?.(parentAc)}
                      title={parentAc ? `Open ${parentAc.name}` : undefined}
                      className="inline-flex max-w-full items-center gap-1 truncate rounded bg-indigo-500/15 px-1.5 py-0.5 text-[10px] font-medium text-indigo-200 hover:bg-indigo-500/25 disabled:cursor-default disabled:hover:bg-indigo-500/15"
                    >
                      {parentAc ? underAreaCommandLabel(parentAc.name) : UNDER_AREA_COMMAND_PREFIX}
                    </button>
                  </div>
                )}
                {onFlyTo && (
                  <div className="px-3 pb-2">
                    <button
                      type="button"
                      data-testid="incident-flyto"
                      onClick={() => onFlyTo(inc.lat, inc.lon)}
                      className="inline-flex items-center gap-1 rounded border border-slate-600 bg-slate-700 px-2 py-1 text-[11px] text-slate-100 hover:bg-slate-600"
                    >
                      <span aria-hidden>🎯</span> Fly to
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* ── #485 Recently deactivated (findability) ────────────────────── */}
      <div className="mt-4 border-t border-slate-700/70 pt-3" data-testid="recently-deactivated">
        <button
          type="button"
          data-testid="recently-deactivated-toggle"
          aria-expanded={deactOpen}
          onClick={() => setDeactOpen((v) => !v)}
          className="flex w-full items-center gap-2 text-left"
        >
          <span aria-hidden className="text-[10px] text-slate-500">
            {deactOpen ? '▾' : '▸'}
          </span>
          <span className="text-[10px] uppercase tracking-wider text-slate-400">
            {RECENTLY_DEACTIVATED_HEADING}
          </span>
          <span
            className="ml-auto shrink-0 rounded bg-slate-700/70 px-1.5 py-0.5 text-[10px] font-medium text-slate-300"
            data-testid="recently-deactivated-count"
          >
            {deactivated.length}
          </span>
        </button>
        {deactOpen && (
          <div className="mt-2">
            <p className="mb-1.5 text-[11px] leading-relaxed text-slate-500">
              {RECENTLY_DEACTIVATED_HELP}
            </p>
            <label className="mb-2 flex items-center gap-1.5 text-[10px] text-slate-400">
              <span className="uppercase tracking-wider">Window</span>
              <select
                data-testid="recently-deactivated-window"
                value={deactWindow}
                onChange={(e) => setDeactWindow(Number(e.target.value))}
                className="rounded border border-slate-600 bg-slate-800 px-1.5 py-0.5 text-[11px] text-slate-100"
              >
                {DEACTIVATED_WINDOW_OPTIONS.map((d) => (
                  <option key={d} value={d}>
                    {d} days
                  </option>
                ))}
              </select>
            </label>
            {deactivated.length === 0 ? (
              <p className="text-[12px] text-slate-500" data-testid="recently-deactivated-empty">
                {RECENTLY_DEACTIVATED_EMPTY}
              </p>
            ) : (
              <ul className="space-y-1.5">
                {deactivated.map((inc) => {
                  const hz = hazardStyle(inc.hazard_type);
                  return (
                    <li key={inc.id} data-testid="recently-deactivated-row">
                      <button
                        type="button"
                        data-testid="recently-deactivated-open"
                        onClick={() => onSelectIncident?.(inc)}
                        title={`Open ${inc.incident_number} — ${inc.name} to reactivate it`}
                        className="block w-full rounded border border-slate-700/70 bg-slate-800/30 px-3 py-1.5 text-left hover:bg-slate-800"
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold"
                            style={{ color: hz.color, backgroundColor: `${hz.color}22` }}
                          >
                            <span aria-hidden>{hz.icon}</span> {hz.label}
                          </span>
                          <span className="min-w-0 flex-1 truncate text-[12px] text-slate-200">
                            {inc.name}
                          </span>
                          <span className="shrink-0 text-[10px] text-slate-500">
                            {deactivatedAgo(inc.closed_at)}
                          </span>
                        </div>
                        <p className="mt-0.5 truncate font-mono text-[10px] text-slate-500">
                          {inc.incident_number}
                        </p>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
